import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
interface LegalSection {
  heading?: string;
  paragraphs: (string | {
    type: 'list';
    items: string[];
  })[];
}
interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  sections: LegalSection[];
}
export function LegalModal({
  isOpen,
  onClose,
  title,
  subtitle,
  sections
}: LegalModalProps) {
  // Lock body scroll when open + close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-196">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.3
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8" onClick={onClose} data-id="element-197">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/85 backdrop-blur-md" data-id="element-198"></div>

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.96,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 20
      }} transition={{
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative w-full max-w-3xl max-h-[88vh] bg-gradient-to-br from-black-100 to-black border border-gold/20 rounded-sm shadow-2xl overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-199">
            {/* Top gold hairline */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent z-10" data-id="element-200"></div>

            {/* Subtle radial glow at top */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[radial-gradient(circle_at_center,rgba(212,175,122,0.1)_0%,transparent_70%)] blur-2xl pointer-events-none" data-id="element-201"></div>

            {/* Close button */}
            <button onClick={onClose} className="absolute top-5 right-5 z-20 w-10 h-10 rounded-full border border-gold/20 bg-black/60 backdrop-blur-sm flex items-center justify-center text-cream/60 hover:text-gold hover:border-gold/40 transition-all" aria-label="Cerrar" data-id="element-202">
              <X size={18} data-id="element-203" />
            </button>

            {/* Scrollable content */}
            <div className="overflow-y-auto px-8 md:px-14 py-12 md:py-16 relative" data-id="element-204">
              {/* Header */}
              <div className="mb-10 text-center" data-id="element-205">
                <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-5" data-id="element-206">
                  Documento Legal
                </p>
                <h2 className="font-display text-3xl md:text-5xl text-cream mb-5 leading-[1.05]" data-id="element-207">
                  {title}
                </h2>
                {subtitle && <p className="text-cream/60 text-base md:text-lg font-light max-w-xl mx-auto leading-relaxed" data-id="element-208">
                    {subtitle}
                  </p>}
                <div className="w-20 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent mx-auto mt-10" data-id="element-209"></div>
              </div>

              {/* Sections */}
              <div className="space-y-10" data-id="element-210">
                {sections.map((section, sIdx) => <section key={sIdx} className="space-y-4" data-id="element-211">
                    {section.heading && <h3 className="font-display text-xl md:text-2xl text-gold/90 mb-3" data-id="element-212">
                        {section.heading}
                      </h3>}
                    {section.paragraphs.map((para, pIdx) => {
                if (typeof para === 'string') {
                  return <p key={pIdx} className="text-cream/75 font-light leading-[1.85] text-[15px] md:text-base" data-id="element-213">
                            {para}
                          </p>;
                }
                return <ul key={pIdx} className="space-y-2 pl-6 my-3" data-id="element-214">
                          {para.items.map((item, iIdx) => <li key={iIdx} className="text-cream/75 font-light leading-[1.85] text-[15px] md:text-base relative before:content-[''] before:absolute before:-left-6 before:top-[14px] before:w-2 before:h-px before:bg-gold/60" data-id="element-215">
                              {item}
                            </li>)}
                        </ul>;
              })}
                  </section>)}
              </div>

              {/* Footer note */}
              <div className="mt-12 pt-8 border-t border-gold/10 text-center" data-id="element-216">
                <p className="text-cream/40 text-sm font-light italic" data-id="element-217">
                  ¿Tienes preguntas sobre este documento?
                </p>
                <a href="mailto:elementaldealsandshop@gmail.com" className="text-gold hover:text-gold-light transition-colors text-sm tracking-wide mt-2 inline-block" data-id="element-218">
                  Contáctanos
                </a>
              </div>
            </div>

            {/* Bottom gold hairline */}
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" data-id="element-219"></div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
// =====================================================================
// Legal content — exported so Footer can use it
// =====================================================================
export const TERMINOS = {
  title: 'Términos de Servicio',
  subtitle: 'Al adquirir cualquiera de nuestros productos digitales, aceptas los siguientes términos y condiciones.',
  sections: [{
    paragraphs: ['Todos los productos ofrecidos en esta página son digitales y entregados electrónicamente. El acceso al contenido se proporciona una vez confirmado el pago.']
  }, {
    heading: 'Naturaleza del contenido',
    paragraphs: ['El contenido incluido en nuestros ebooks, prompts, guías y materiales digitales es únicamente con fines educativos e informativos. No garantizamos resultados específicos, ingresos, crecimiento de negocio, viralidad, ventas ni resultados profesionales particulares, ya que estos dependen de múltiples factores externos y del uso individual de cada usuario.']
  }, {
    heading: 'Uso permitido',
    paragraphs: ['Está prohibida la reventa, redistribución, reproducción o compartición no autorizada de nuestros productos digitales, parcial o totalmente.']
  }, {
    heading: 'Modificaciones',
    paragraphs: ['Nos reservamos el derecho de actualizar, modificar o mejorar nuestros productos y contenidos en cualquier momento sin previo aviso.']
  }, {
    heading: 'Aceptación',
    paragraphs: ['Al utilizar este sitio web y adquirir nuestros productos, aceptas estos términos en su totalidad.']
  }]
};
export const PRIVACIDAD = {
  title: 'Política de Privacidad',
  subtitle: 'Tu privacidad es importante para nosotros.',
  sections: [{
    heading: 'Uso de tu información',
    paragraphs: ['La información personal proporcionada durante el proceso de compra, incluyendo nombre, correo electrónico y datos de pago, será utilizada únicamente para los siguientes fines:', {
      type: 'list' as const,
      items: ['Procesar tu compra', 'Entregarte el producto digital', 'Brindarte soporte', 'Enviarte información relacionada con nuestros productos y actualizaciones']
    }]
  }, {
    heading: 'Compartición con terceros',
    paragraphs: ['No vendemos, alquilamos ni compartimos tu información personal con terceros para fines comerciales.']
  }, {
    heading: 'Seguridad de pagos',
    paragraphs: ['Los pagos son procesados mediante plataformas externas seguras, por lo que no almacenamos información sensible de tarjetas de crédito o métodos de pago.']
  }, {
    heading: 'Aceptación',
    paragraphs: ['Al utilizar este sitio web, aceptas nuestra política de privacidad.']
  }]
};
export const REEMBOLSOS = {
  title: 'Política de Reembolso',
  subtitle: 'Debido a la naturaleza digital de nuestros productos, todas las ventas son consideradas finales.',
  sections: [{
    heading: 'Garantía de satisfacción',
    paragraphs: ['Sin embargo, ofrecemos una garantía limitada de reembolso de 7 días desde la fecha de compra en caso de inconvenientes técnicos, acceso no recibido o situaciones razonables evaluadas por nuestro equipo.']
  }, {
    heading: 'Después del periodo de garantía',
    paragraphs: ['Después de transcurridos los 7 días, no se realizarán reembolsos.']
  }, {
    heading: 'Condiciones',
    paragraphs: ['Nos reservamos el derecho de rechazar solicitudes de reembolso en casos de abuso, uso indebido o fraude.']
  }, {
    heading: 'Soporte',
    paragraphs: ['Si necesitas asistencia, puedes contactarnos a través del correo electrónico indicado en esta página.']
  }]
};